/**
 * Theme Manager
 * Handles dynamic CSS variable injection and theme management
 */

// Default theme colors
const DEFAULT_THEME = {
    primary: '#8B4513',
    primaryDark: '#654321',
    primaryLight: '#A0522D',
    secondary: '#FFD700',
    secondaryDark: '#DAA520',
    secondaryLight: '#FFED4E',
    accent: '#FC5A8D',
    background: '#FFFFFF',
    backgroundAlt: '#F9FAFB',
    surface: '#FFFFFF',
    text: '#1F2937',
    textSecondary: '#6B7280',
    textLight: '#9CA3AF',
    border: '#E5E7EB',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6'
}

// Theme presets
export const THEME_PRESETS = {
    classic: {
        name: 'Classic Bakery',
        description: 'Warm browns and creamy tones',
        colors: {
            primary: '#8B4513',
            primaryDark: '#654321',
            primaryLight: '#A0522D',
            secondary: '#FFD700',
            secondaryDark: '#DAA520',
            secondaryLight: '#FFED4E',
            accent: '#D2691E',
            background: '#FFFAF0',
            backgroundAlt: '#FFF8DC',
            surface: '#FFFFFF'
        },
        fonts: {
            display: 'Playfair Display',
            body: 'Inter'
        }
    },
    modern: {
        name: 'Modern Pastry',
        description: 'Pink and purple gradients',
        colors: {
            primary: '#EC4899',
            primaryDark: '#BE185D',
            primaryLight: '#F9A8D4',
            secondary: '#8B5CF6',
            secondaryDark: '#6D28D9',
            secondaryLight: '#C4B5FD',
            accent: '#F472B6',
            background: '#FDF4FF',
            backgroundAlt: '#FAF5FF',
            surface: '#FFFFFF'
        },
        fonts: {
            display: 'Poppins',
            body: 'Lato'
        }
    },
    elegant: {
        name: 'Elegant Patisserie',
        description: 'Gold and black luxury',
        colors: {
            primary: '#000000',
            primaryDark: '#111827',
            primaryLight: '#374151',
            secondary: '#D4AF37',
            secondaryDark: '#B8941E',
            secondaryLight: '#F4D77E',
            accent: '#D4AF37',
            background: '#FFFFFF',
            backgroundAlt: '#F9FAFB',
            surface: '#FFFFFF'
        },
        fonts: {
            display: 'Cormorant Garamond',
            body: 'Lora'
        }
    },
    fresh: {
        name: 'Fresh & Natural',
        description: 'Green and earth tones',
        colors: {
            primary: '#059669',
            primaryDark: '#047857',
            primaryLight: '#10B981',
            secondary: '#92400E',
            secondaryDark: '#78350F',
            secondaryLight: '#B45309',
            accent: '#84CC16',
            background: '#F0FDF4',
            backgroundAlt: '#ECFDF5',
            surface: '#FFFFFF'
        },
        fonts: {
            display: 'Merriweather',
            body: 'Open Sans'
        }
    }
}

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color code
 * @returns {string} RGB values (e.g., "255, 255, 255")
 */
export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '0, 0, 0'
}

/**
 * Generate color shades
 * @param {string} baseColor - Base hex color
 * @returns {Object} Color shades
 */
export function generateColorShades(baseColor) {
    // This is a simplified version. In production, use a color manipulation library
    const rgb = hexToRgb(baseColor)
    return {
        50: `rgb(${rgb.split(',').map(v => Math.min(255, parseInt(v) + 200)).join(',')})`,
        100: `rgb(${rgb.split(',').map(v => Math.min(255, parseInt(v) + 150)).join(',')})`,
        200: `rgb(${rgb.split(',').map(v => Math.min(255, parseInt(v) + 100)).join(',')})`,
        300: `rgb(${rgb.split(',').map(v => Math.min(255, parseInt(v) + 50)).join(',')})`,
        400: `rgb(${rgb.split(',').map(v => Math.min(255, parseInt(v) + 25)).join(',')})`,
        500: baseColor,
        600: `rgb(${rgb.split(',').map(v => Math.max(0, parseInt(v) - 25)).join(',')})`,
        700: `rgb(${rgb.split(',').map(v => Math.max(0, parseInt(v) - 50)).join(',')})`,
        800: `rgb(${rgb.split(',').map(v => Math.max(0, parseInt(v) - 75)).join(',')})`,
        900: `rgb(${rgb.split(',').map(v => Math.max(0, parseInt(v) - 100)).join(',')})`
    }
}

/**
 * Apply theme to document
 * @param {Object} branding - Branding configuration
 */
export function applyTheme(branding) {
    if (!branding) {
        resetTheme()
        return
    }

    const root = document.documentElement

    // Apply colors
    if (branding.colors) {
        const colors = { ...DEFAULT_THEME, ...branding.colors }

        // Primary colors
        if (colors.primary) {
            root.style.setProperty('--color-primary', colors.primary)
            root.style.setProperty('--color-primary-rgb', hexToRgb(colors.primary))
        }
        if (colors.primaryDark) {
            root.style.setProperty('--color-primary-dark', colors.primaryDark)
        }
        if (colors.primaryLight) {
            root.style.setProperty('--color-primary-light', colors.primaryLight)
        }

        // Secondary colors
        if (colors.secondary) {
            root.style.setProperty('--color-secondary', colors.secondary)
            root.style.setProperty('--color-secondary-rgb', hexToRgb(colors.secondary))
        }
        if (colors.secondaryDark) {
            root.style.setProperty('--color-secondary-dark', colors.secondaryDark)
        }
        if (colors.secondaryLight) {
            root.style.setProperty('--color-secondary-light', colors.secondaryLight)
        }

        // Accent color
        if (colors.accent) {
            root.style.setProperty('--color-accent', colors.accent)
            root.style.setProperty('--color-accent-rgb', hexToRgb(colors.accent))
        }

        // Background colors
        if (colors.background) {
            root.style.setProperty('--color-background', colors.background)
        }
        if (colors.backgroundAlt) {
            root.style.setProperty('--color-background-alt', colors.backgroundAlt)
        }
        if (colors.surface) {
            root.style.setProperty('--color-surface', colors.surface)
        }

        // Text colors
        if (colors.text) {
            root.style.setProperty('--color-text', colors.text)
        }
        if (colors.textSecondary) {
            root.style.setProperty('--color-text-secondary', colors.textSecondary)
        }
        if (colors.textLight) {
            root.style.setProperty('--color-text-light', colors.textLight)
        }

        // Border color
        if (colors.border) {
            root.style.setProperty('--color-border', colors.border)
        }

        // Status colors
        if (colors.success) root.style.setProperty('--color-success', colors.success)
        if (colors.warning) root.style.setProperty('--color-warning', colors.warning)
        if (colors.error) root.style.setProperty('--color-error', colors.error)
        if (colors.info) root.style.setProperty('--color-info', colors.info)
    }

    // Apply fonts
    if (branding.fonts) {
        if (branding.fonts.display) {
            root.style.setProperty('--font-display', branding.fonts.display)
        }
        if (branding.fonts.body) {
            root.style.setProperty('--font-body', branding.fonts.body)
        }
        if (branding.fonts.mono) {
            root.style.setProperty('--font-mono', branding.fonts.mono)
        }
    }

    // Apply spacing customizations
    if (branding.spacing) {
        if (branding.spacing.borderRadius) {
            root.style.setProperty('--border-radius', branding.spacing.borderRadius)
        }
        if (branding.spacing.containerPadding) {
            root.style.setProperty('--container-padding', branding.spacing.containerPadding)
        }
    }

    // Apply dark mode if enabled
    if (branding.darkMode && branding.darkMode.enabled) {
        applyDarkMode(branding.darkMode)
    }

    // Apply custom CSS if provided
    if (branding.customCSS) {
        injectCustomCSS(branding.customCSS)
    }

    // Store current theme
    localStorage.setItem('currentTheme', JSON.stringify(branding))
}

/**
 * Apply dark mode theme
 * @param {Object} darkModeConfig - Dark mode configuration
 */
export function applyDarkMode(darkModeConfig) {
    const root = document.documentElement

    if (darkModeConfig.enabled) {
        root.classList.add('dark')

        // Override colors for dark mode
        if (darkModeConfig.colors) {
            Object.entries(darkModeConfig.colors).forEach(([key, value]) => {
                root.style.setProperty(`--color-${key}-dark`, value)
            })
        }
    } else {
        root.classList.remove('dark')
    }
}

/**
 * Inject custom CSS
 * @param {string} css - Custom CSS string
 */
export function injectCustomCSS(css) {
    // Remove existing custom CSS
    const existingStyle = document.getElementById('custom-branding-css')
    if (existingStyle) {
        existingStyle.remove()
    }

    // Create and inject new style element
    const style = document.createElement('style')
    style.id = 'custom-branding-css'
    style.textContent = css
    document.head.appendChild(style)
}

/**
 * Reset theme to default
 */
export function resetTheme() {
    const root = document.documentElement

    // Reset to default colors
    Object.entries(DEFAULT_THEME).forEach(([key, value]) => {
        const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase()
        root.style.setProperty(`--color-${cssKey}`, value)
        if (['primary', 'secondary', 'accent'].includes(key)) {
            root.style.setProperty(`--color-${cssKey}-rgb`, hexToRgb(value))
        }
    })

    // Reset fonts
    root.style.setProperty('--font-display', 'Playfair Display')
    root.style.setProperty('--font-body', 'Inter')
    root.style.setProperty('--font-mono', 'monospace')

    // Remove dark mode
    root.classList.remove('dark')

    // Remove custom CSS
    const customStyle = document.getElementById('custom-branding-css')
    if (customStyle) {
        customStyle.remove()
    }

    // Clear stored theme
    localStorage.removeItem('currentTheme')
}

/**
 * Get current theme configuration
 * @returns {Object} Current theme configuration
 */
export function getCurrentTheme() {
    const stored = localStorage.getItem('currentTheme')
    return stored ? JSON.parse(stored) : null
}

/**
 * Apply theme preset
 * @param {string} presetName - Name of the preset (classic, modern, elegant, fresh)
 */
export function applyThemePreset(presetName) {
    const preset = THEME_PRESETS[presetName]
    if (!preset) {
        console.error(`Theme preset "${presetName}" not found`)
        return
    }

    applyTheme({
        colors: preset.colors,
        fonts: preset.fonts,
        preset: presetName
    })
}

/**
 * Load Google Fonts
 * @param {Array<string>} fonts - Array of Google Font names
 */
export function loadGoogleFonts(fonts) {
    if (!fonts || !Array.isArray(fonts) || fonts.length === 0) return

    // Remove existing Google Fonts link
    const existingLink = document.getElementById('google-fonts-branding')
    if (existingLink) {
        existingLink.remove()
    }

    // Create fonts URL
    const fontsParam = fonts.map(font => font.replace(/ /g, '+')).join('&family=')
    const url = `https://fonts.googleapis.com/css2?family=${fontsParam}:wght@300;400;500;600;700&display=swap`

    // Create and inject link
    const link = document.createElement('link')
    link.id = 'google-fonts-branding'
    link.rel = 'stylesheet'
    link.href = url
    document.head.appendChild(link)
}

/**
 * Generate theme preview
 * @param {Object} colors - Color configuration
 * @returns {Object} Preview data
 */
export function generateThemePreview(colors) {
    return {
        primary: colors.primary || DEFAULT_THEME.primary,
        secondary: colors.secondary || DEFAULT_THEME.secondary,
        accent: colors.accent || DEFAULT_THEME.accent,
        background: colors.background || DEFAULT_THEME.background,
        text: colors.text || DEFAULT_THEME.text
    }
}

/**
 * Validate color format
 * @param {string} color - Color string
 * @returns {boolean} Is valid
 */
export function isValidColor(color) {
    if (!color) return false

    // Check hex format
    if (/^#[0-9A-F]{6}$/i.test(color)) return true

    // Check rgb format
    if (/^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/i.test(color)) return true

    // Check rgba format
    if (/^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/i.test(color)) return true

    // Check named colors (basic check)
    const s = new Option().style
    s.color = color
    return s.color !== ''
}

/**
 * Get contrasting text color (black or white)
 * @param {string} backgroundColor - Background color hex
 * @returns {string} Contrasting text color
 */
export function getContrastingTextColor(backgroundColor) {
    const rgb = hexToRgb(backgroundColor).split(',').map(v => parseInt(v.trim()))
    const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255
    return luminance > 0.5 ? '#000000' : '#FFFFFF'
}

export default {
    applyTheme,
    resetTheme,
    applyThemePreset,
    applyDarkMode,
    injectCustomCSS,
    getCurrentTheme,
    loadGoogleFonts,
    generateThemePreview,
    isValidColor,
    getContrastingTextColor,
    hexToRgb,
    generateColorShades,
    THEME_PRESETS,
    DEFAULT_THEME
}
